import type {
    AddPackageToQuoteMutationFn,
    CreateQuoteMutationFn,
    ListingFieldsFragment,
    QuoteFieldsFragment,
    QuotePackageFieldsFragment,
    UpdatePackageOnQuoteMutationFn,
} from "~graphql/generated/graphql"
import type { OnCreateOrCompletedFunc } from "~utils/use-checkout-flow-draft-quote"
import useCheckoutFlowDraftQuote from "~utils/use-checkout-flow-draft-quote"
import useAddOrUpdateQuotePackage from "~utils/use-add-or-update-quote-package"

export function useCheckoutFlowNavButtonsProps({
    listing,
    quote,
    quotePackageBeingEdited,
    onCreateQuoteCompleted,
}: {
    listing: ListingFieldsFragment | null
    quote?: QuoteFieldsFragment | null
    quotePackageBeingEdited?: QuotePackageFieldsFragment | null
    onCreateQuoteCompleted?: OnCreateOrCompletedFunc
}) {
    const { createQuote, isLoadingCreateQuote, draftQuoteError, isTripValid } =
        useCheckoutFlowDraftQuote({
            listingId: listing?.id,
            onCreateQuoteCompleted,
        })

    const {
        addPackageToQuote,
        updatePackageOnQuote,
        isLoading: isLoadingAddOrUpdate,
    } = useAddOrUpdateQuotePackage({
        listingId: listing?.id,
        quoteId: quote?.id,
        quotePackageId: quotePackageBeingEdited?.id,
    })

    const isLoading = isLoadingCreateQuote || isLoadingAddOrUpdate
    const isContinueDisabled = !isTripValid
    const onContinueClickOverride = getOnContinueHandler({
        createQuote,
        addPackageToQuote,
        updatePackageOnQuote,
        quote,
        quotePackageBeingEdited,
    })

    return {
        onContinueClickOverride,
        isLoading,
        isContinueDisabled,
        draftQuoteError,
    }
}

function getOnContinueHandler({
    createQuote,
    addPackageToQuote,
    updatePackageOnQuote,
    quote,
    quotePackageBeingEdited,
}: {
    createQuote: CreateQuoteMutationFn
    addPackageToQuote: AddPackageToQuoteMutationFn
    updatePackageOnQuote: UpdatePackageOnQuoteMutationFn
    quote?: QuoteFieldsFragment | null
    quotePackageBeingEdited?: QuotePackageFieldsFragment | null
}) {
    if (!quote) return createQuote

    return quotePackageBeingEdited ? updatePackageOnQuote : addPackageToQuote
}
